import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { api, type Target, type TargetStatus, type Voice } from '@/api/client'
import { useTargets } from '@/lib/queries'
import { TONE_CLASS } from '@/lib/status'
import { cn } from '@/lib/utils'
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  FieldGroup,
  Input,
  KeyValue,
  PageHeader,
  PlatformMark,
  Select,
  Skeleton,
  StatusPill,
  errorText,
  useQueryParam,
  useToast,
} from '@/ui'
import { DisableAccountDialog } from './DisableAccountDialog'
import { buildCredentials, CredentialFields, credentialsComplete, credentialsTouched, emptyCred } from './Credentials'
import {
  CheckLine,
  PLATFORM_LABEL,
  tokenInfo,
  useAccountUsage,
  useInvalidateAccounts,
  useTestAccount,
} from './shared'
import { emptyVoice, PRESETS, VoiceFields } from './voice'

type Tab = 'connection' | 'voice' | 'usage'
const TABS: { id: Tab; label: string }[] = [
  { id: 'connection', label: '연결' },
  { id: 'voice', label: '말투' },
  { id: 'usage', label: '사용처' },
]

/** Saves an account. A voice is always sent: the server replaces it, so
 *  leaving it out would blank it. Credentials are sent only when given — an
 *  update without them keeps the stored ones. */
function useSaveAccount(t: Target) {
  const invalidate = useInvalidateAccounts()
  const toast = useToast()
  return useMutation({
    mutationFn: (patch: {
      name?: string
      status?: TargetStatus
      voice?: Voice
      credentials?: ReturnType<typeof buildCredentials>
    }) =>
      api.updateTarget(t.id, {
        name: patch.name ?? t.name,
        platform: t.platform,
        status: patch.status ?? t.status,
        voice: patch.voice ?? t.voice ?? emptyVoice,
        ...(patch.credentials ? { credentials: patch.credentials } : {}),
      }),
    onSuccess: () => toast.success('저장했다'),
    onSettled: () => invalidate(),
  })
}

function ConnectionTab({ t }: { t: Target }) {
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateAccounts()
  const save = useSaveAccount(t)
  const usage = useAccountUsage().byId(t.id)
  const token = tokenInfo(t)
  const [name, setName] = useState(t.name)
  const [status, setStatus] = useState<TargetStatus>(t.status)
  const [cred, setCred] = useState(emptyCred)
  const [deleting, setDeleting] = useState(false)
  const [disabling, setDisabling] = useState(false)

  useEffect(() => {
    setName(t.name)
    setStatus(t.status)
  }, [t.name, t.status])

  const touched = credentialsTouched(t.platform, cred)
  const complete = credentialsComplete(t.platform, cred)
  const dirty = name.trim() !== t.name || status !== t.status || touched
  const blocked = name.trim() === '' || (touched && !complete)

  const doSave = async () => {
    const replacing = touched && complete
    try {
      await save.mutateAsync({
        name: name.trim(),
        status,
        credentials: replacing ? buildCredentials(t.platform, cred) : undefined,
      })
      setCred(emptyCred())
      if (replacing) {
        toast.info(
          t.platform === 'reddit_browser' || t.platform === 'x' || t.platform === 'instagram' || t.platform === 'bluesky'
            ? '새 자격이다 — 연결 테스트를 다시 누른다'
            : '새 토큰이다 — 연결 테스트를 다시 누른다',
        )
      }
    } catch {
      /* shown below from save.error */
    }
  }
  // Switching an account off is asked first, with the places that use it.
  const submit = () => {
    if (status === 'disabled' && t.status !== 'disabled') setDisabling(true)
    else void doSave()
  }

  const remove = useMutation({
    mutationFn: () => api.deleteTarget(t.id),
    onSuccess: async () => {
      await invalidate()
      toast.success(`${t.name} 계정을 지웠다`)
      navigate('/accounts')
    },
  })

  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-border bg-card p-4">
        <KeyValue
          items={[
            { label: '플랫폼', value: PLATFORM_LABEL[t.platform] },
            { label: '상태', value: <StatusPill domain="account" status={t.status} /> },
            { label: '마지막 점검', value: <CheckLine ok={t.last_check_ok} msg={t.last_check_msg} /> },
            ...(token
              ? [
                  {
                    label: '토큰 만료',
                    value: (
                      <span className={cn(token.tone && TONE_CLASS[token.tone].text)}>
                        <b className="tabular-nums">{token.label}</b>
                        {token.days !== null && <span className="ml-2 text-muted-foreground">{token.detail}</span>}
                        {token.days === null && (
                          <span className="ml-2 text-muted-foreground">{token.detail}</span>
                        )}
                      </span>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-card p-4">
        <h2 className="text-section font-semibold">계정 정보</h2>
        <FieldGroup columns={2}>
          <Field label="이름">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="상태">
            <Select value={status} onChange={(e) => setStatus(e.target.value as TargetStatus)}>
              <option value="active">사용</option>
              <option value="disabled">중지</option>
            </Select>
          </Field>
        </FieldGroup>

        <FieldGroup
          title="자격 바꾸기"
          description="저장된 자격은 서버에 암호화돼 있고 화면에는 나오지 않는다. 칸을 모두 비워 두면 그대로 둔다. 바꾸려면 이 플랫폼에 필요한 칸을 전부 채운다."
        >
          <CredentialFields platform={t.platform} value={cred} onChange={setCred} />
          {touched && !complete && <p className="text-meta text-status-failed">비어 있는 칸이 있다. 전부 채워야 교체된다.</p>}
        </FieldGroup>

        {save.error != null && <ErrorState title="저장하지 못했다" error={save.error} />}
        <DisableAccountDialog
          t={t}
          open={disabling}
          onOpenChange={setDisabling}
          onConfirm={async () => {
            await doSave()
            setDisabling(false)
          }}
        />
        <div className="flex gap-2">
          <Button variant="primary" loading={save.isPending} disabled={!dirty || blocked} onClick={submit}>
            저장
          </Button>
          {dirty && (
            <Button
              variant="ghost"
              onClick={() => {
                setName(t.name)
                setStatus(t.status)
                setCred(emptyCred())
              }}
            >
              되돌리기
            </Button>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-status-failed/30 p-4">
        <h2 className="text-section font-semibold">계정 삭제</h2>
        <p className="mt-1 text-table text-muted-foreground">
          {usage.campaigns.length + usage.apps.length > 0
            ? `캠페인 ${usage.campaigns.length}개·리딤 앱 ${usage.apps.length}개가 이 계정을 쓴다. 먼저 연결을 풀어야 지워진다.`
            : '이 계정을 쓰는 캠페인과 앱이 없다.'}
        </p>
        {remove.error != null && <p className="mt-2 text-table text-status-failed">{errorText(remove.error)}</p>}
        <Button className="mt-3" variant="danger" icon={Trash2} onClick={() => setDeleting(true)}>
          삭제
        </Button>
        <ConfirmDialog
          open={deleting}
          onOpenChange={setDeleting}
          title={`${t.name} 계정을 지울까`}
          description="저장된 자격과 말투가 함께 지워진다."
          confirmLabel="삭제"
          confirmVariant="destructive"
          onConfirm={async () => {
            try {
              await remove.mutateAsync()
            } catch {
              /* the message shows in the section */
            }
            setDeleting(false)
          }}
        />
      </section>
    </div>
  )
}

function VoiceTab({ t }: { t: Target }) {
  const save = useSaveAccount(t)
  const saved = t.voice ?? emptyVoice
  const [voice, setVoice] = useState<Voice>(saved)
  const dirty = JSON.stringify(voice) !== JSON.stringify(saved)
  // A reload of the account (after a save) brings the stored voice back in.
  const key = JSON.stringify(saved)
  useEffect(() => setVoice(saved), [key]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div>
        <h2 className="text-section font-semibold">말투</h2>
        <p className="mt-1 text-table text-muted-foreground">
          계정이 어떻게 말하는지. 말투는 계정에 붙고 캠페인은 “무엇을 다루는지”만 정한다. 앱이 늘어도 말투는 늘지 않는다.
        </p>
      </div>
      {!saved.persona && (
        <p className={cn('text-table', TONE_CLASS.publishing.text)}>말투 없음 — 이 계정의 글은 캠페인 페르소나로 쓰인다.</p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-meta text-muted-foreground">프리셋</span>
        {PRESETS.map((p) => (
          <Button key={p.id} size="sm" title={p.note} onClick={() => setVoice(p.voice)}>
            {p.label}
          </Button>
        ))}
      </div>
      <VoiceFields voice={voice} onChange={setVoice} />
      {save.error != null && <ErrorState title="저장하지 못했다" error={save.error} />}
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          loading={save.isPending}
          disabled={!dirty}
          onClick={() => save.mutate({ voice })}
        >
          저장
        </Button>
        {dirty && (
          <Button variant="ghost" onClick={() => setVoice(saved)}>
            되돌리기
          </Button>
        )}
      </div>
    </section>
  )
}

function UsageTab({ t }: { t: Target }) {
  const usage = useAccountUsage()
  const used = usage.byId(t.id)
  if (!usage.ready) return <Skeleton className="h-32" />
  if (used.campaigns.length + used.apps.length === 0) {
    return (
      <EmptyState
        title="이 계정을 쓰는 곳이 없다"
        description="캠페인은 상세의 페이싱에서, 리딤 앱은 앱 설정의 데스크 계정에서 이 계정을 고른다."
      />
    )
  }
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <section className="rounded-lg border border-border bg-card p-4">
        <h2 className="text-section font-semibold">캠페인 {used.campaigns.length}</h2>
        <ul className="mt-2 divide-y divide-border">
          {used.campaigns.map((c) => (
            <li key={c.ID} className="flex items-center justify-between gap-2 py-2 text-body">
              <Link to={`/campaigns/${c.ID}`} className="min-w-0 truncate font-medium hover:underline">
                {c.Name}
              </Link>
              <span className="shrink-0 text-meta text-muted-foreground">
                {Object.entries(c.PlatformConfigs ?? {})
                  .filter(([, p]) => p.target_id === t.id)
                  .map(([k]) => k)
                  .join(', ')}
              </span>
            </li>
          ))}
          {used.campaigns.length === 0 && <li className="py-2 text-table text-muted-foreground">없음</li>}
        </ul>
      </section>
      <section className="rounded-lg border border-border bg-card p-4">
        <h2 className="text-section font-semibold">리딤 앱 {used.apps.length}</h2>
        <ul className="mt-2 divide-y divide-border">
          {used.apps.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-2 py-2 text-body">
              <Link to={`/redeem/${a.id}`} className="min-w-0 truncate font-medium hover:underline">
                {a.name}
              </Link>
              <span className="shrink-0 text-meta text-muted-foreground">
                {a.auto_handout === 1 ? '자동 발송' : '수동 발송'}
              </span>
            </li>
          ))}
          {used.apps.length === 0 && <li className="py-2 text-table text-muted-foreground">없음</li>}
        </ul>
      </section>
    </div>
  )
}

/** /accounts/:id: one account — connection, voice and where it is used. */
export default function AccountDetail() {
  const id = Number(useParams().id)
  const q = useTargets()
  const [tabParam, setTab] = useQueryParam('tab', 'connection')
  const test = useTestAccount()
  const tab: Tab = TABS.some((x) => x.id === tabParam) ? (tabParam as Tab) : 'connection'
  if (!Number.isInteger(id) || id <= 0) return <Navigate to="/accounts" replace />

  const t = q.data?.find((x) => x.id === id)
  const parent = { to: '/accounts', label: '계정' }
  if (!q.data) {
    return (
      <>
        <PageHeader title="계정" parent={parent} />
        {q.error ? <ErrorState title="계정을 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} /> : <Skeleton className="h-48" />}
      </>
    )
  }
  if (!t) {
    return (
      <>
        <PageHeader title="계정" parent={parent} />
        <EmptyState title="없는 계정이다" description="지워졌거나 주소가 틀렸다." />
      </>
    )
  }

  return (
    <>
      <PageHeader
        parent={parent}
        title={
          <span className="inline-flex items-center gap-2">
            <PlatformMark platform={t.platform} />
            {t.name}
          </span>
        }
        description={PLATFORM_LABEL[t.platform]}
        actions={
          <Button loading={test.isPending} onClick={() => test.mutate(t.id)}>
            연결 테스트
          </Button>
        }
      >
        <div role="tablist" aria-label={`${t.name} 화면`} className="-mx-1 flex overflow-x-auto border-b border-border px-1">
          {TABS.map((x) => (
            <button
              key={x.id}
              role="tab"
              type="button"
              aria-selected={tab === x.id}
              onClick={() => setTab(x.id)}
              className={cn(
                'relative shrink-0 whitespace-nowrap px-3 py-2 text-body font-medium transition-colors',
                tab === x.id
                  ? 'text-foreground after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:bg-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {x.label}
            </button>
          ))}
        </div>
      </PageHeader>
      <div className="max-w-3xl">
        {tab === 'connection' && <ConnectionTab t={t} />}
        {tab === 'voice' && <VoiceTab t={t} />}
        {tab === 'usage' && <UsageTab t={t} />}
      </div>
    </>
  )
}
